import React, { useMemo } from 'react'
import { SegmentVariant } from './typings'

interface SegmentShapeProps {
  color: string
  height: number
  width: number
  variant: SegmentVariant
  radius?: number
  transitionTime?: number
}

/**
 * Unified SVG shape component that replaces the four separate SVG components
 * (LeftProgressNode, MiddleProgressNode, RightProgressNode, SingleProgressNode).
 *
 * Renders the correct chevron/arrow shape based on the `variant` prop.
 */
export const SegmentShape: React.FC<SegmentShapeProps> = React.memo(
  ({
    color = '#18325A',
    height = 33,
    width: rawWidth = 100,
    variant,
    radius = 4,
    transitionTime = 0.5,
  }) => {
    const width = Math.ceil(rawWidth)
    const midY = Math.ceil(height / 2)
    const r = radius
    const halfR = r / 2

    const pathData = useMemo(() => {
      switch (variant) {
        case 'left':
          return buildLeftPath(width, height, midY, r, halfR)
        case 'right':
          return buildRightPath(width, height, midY, r, halfR)
        case 'single':
          return buildSinglePath(width, height, r, halfR)
        case 'middle':
          return null // middle uses <polygon>
      }
    }, [variant, width, height, midY, r, halfR])

    const polygonPoints = useMemo(() => {
      if (variant !== 'middle') return undefined
      return `0,0 ${midY},${midY} 0,${height} ${width - midY},${height} ${width},${midY} ${width - midY},0`
    }, [variant, width, height, midY])

    const fillStyle = useMemo(
      () => ({
        fill: color,
        transition: `fill ${transitionTime}s`,
      }),
      [color, transitionTime],
    )

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
      >
        {variant === 'middle' ? (
          <polygon style={fillStyle} points={polygonPoints} />
        ) : (
          <path style={fillStyle} d={pathData!} />
        )}
      </svg>
    )
  },
)

SegmentShape.displayName = 'SegmentShape'

// ==================== Path Builders ====================

function buildLeftPath(
  w: number,
  h: number,
  midY: number,
  r: number,
  halfR: number,
): string {
  return `M${w - midY},${h}H${r}c-${halfR},0-${r}-${halfR}-${r}-${r}V${r}c0-${halfR},${halfR}-${r},${r}-${r}H${w - midY}l${midY},${midY}L${w - midY},${h}z`
}

function buildRightPath(
  w: number,
  h: number,
  midY: number,
  r: number,
  halfR: number,
): string {
  return `M${w - r},${h}c${halfR},0,${r}-${halfR},${r}-${r}V${r}c0-${halfR}-${halfR}-${r}-${r}-${r}H-0l${midY},${midY}L-0,${h}H${w - r}z`
}

function buildSinglePath(
  w: number,
  h: number,
  r: number,
  halfR: number,
): string {
  return `M${r},${h}c-${halfR},0-${r}-${halfR}-${r}-${r}V${r}c0-${halfR},${halfR}-${r},${r}-${r}h${w - r * 2}c${halfR},0,${r},${halfR},${r},${r}v${h - r * 2}c0,${halfR}-${halfR},${r}-${r},${r}H${r}z`
}
